import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Star,
  Send,
  RefreshCw,
  Check,
  X,
  ArrowUp,
  ArrowDown,
  ExternalLink,
  Mail,
  MailCheck,
  MailX,
  Loader2,
} from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import type { UserDetail } from '../types';

interface Props {
  detail: UserDetail;
  onChange: () => void;
}

const TRIGGER_LABELS: Record<string, string> = {
  first_credit_use: 'First credit use',
  bulk_existing_user: 'Bulk invite (existing user)',
  admin_resend: 'Admin re-send',
};

export function ReviewPanel({ detail, onChange }: Props) {
  const { toast } = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const review = detail.review;
  const invitation = detail.review_invitation;

  const setBusyDo = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    try {
      await fn();
    } finally {
      setBusy(null);
    }
  };

  const updateReview = async (patch: Record<string, any>, successMsg: string) => {
    if (!review) return;
    const { error } = await supabase.from('reviews').update(patch).eq('id', review.id);
    if (error) {
      toast({ title: 'Update failed', description: error.message, variant: 'destructive' });
      return;
    }
    toast({ title: successMsg });
    onChange();
  };

  const handleApprove = () =>
    setBusyDo('approve', () =>
      updateReview(
        { status: 'approved', reviewed_at: new Date().toISOString() },
        'Review approved'
      )
    );

  const handleReject = () =>
    setBusyDo('reject', () =>
      updateReview(
        { status: 'rejected', reviewed_at: new Date().toISOString() },
        'Review rejected'
      )
    );

  const handleToggleFeatured = (next: boolean) =>
    setBusyDo('featured', () =>
      updateReview(
        { is_featured: next },
        next ? 'Now showing on landing page' : 'Hidden from landing page'
      )
    );

  const handleMoveOrder = (delta: number) =>
    setBusyDo('order', () =>
      updateReview(
        { featured_order: Math.max(0, (review?.featured_order ?? 0) + delta) },
        'Order updated'
      )
    );

  const handleSendInvite = (force: boolean) =>
    setBusyDo('invite', async () => {
      const { data, error } = await supabase.functions.invoke('trigger-review-invitation', {
        body: { user_id: detail.user.id, ...(force ? { force: true } : {}) },
      });
      if (error) {
        toast({ title: 'Invitation failed', description: error.message, variant: 'destructive' });
        return;
      }
      if (data?.skipped) {
        toast({ title: 'Skipped', description: `Reason: ${data.skipped}` });
      } else if (data?.success) {
        toast({ title: force ? 'Invitation re-sent' : 'Invitation sent' });
      }
      onChange();
    });

  return (
    <div className="space-y-5">
      {review ? (
        <>
          {/* Review preview card */}
          <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
            <div className="p-5 space-y-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    {review.photo_url && (
                      <AvatarImage src={review.photo_url} alt={review.display_name} />
                    )}
                    <AvatarFallback>{review.display_name?.[0] ?? '?'}</AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-semibold">{review.display_name}</div>
                    {review.display_role && (
                      <div className="text-xs text-muted-foreground">{review.display_role}</div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={cn(
                          'h-4 w-4',
                          i < review.rating
                            ? 'fill-warning text-warning'
                            : 'text-muted-foreground/30'
                        )}
                      />
                    ))}
                  </div>
                  <Badge
                    variant={
                      review.status === 'approved'
                        ? 'default'
                        : review.status === 'rejected'
                          ? 'destructive'
                          : 'secondary'
                    }
                    className="capitalize"
                  >
                    {review.status}
                  </Badge>
                  {review.is_featured && (
                    <Badge
                      variant="outline"
                      className="border-warning/40 text-warning bg-warning/5"
                    >
                      ⭐ Featured
                    </Badge>
                  )}
                </div>
              </div>

              <p className="text-sm whitespace-pre-wrap leading-relaxed">{review.review_text}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 px-5 py-3 border-t border-border/60 bg-muted/20 text-xs">
              <Stat label="Submitted" value={format(new Date(review.created_at), 'dd.MM.yy HH:mm')} />
              <Stat
                label="Reviewed"
                value={
                  review.reviewed_at ? format(new Date(review.reviewed_at), 'dd.MM.yy HH:mm') : '—'
                }
              />
              <Stat label="Order" value={String(review.featured_order)} />
              <Stat label="Credits granted" value={review.credits_granted ? 'Yes' : 'No'} />
            </div>
          </div>

          {/* Moderation card */}
          <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
            <div className="p-4 border-b border-border/60">
              <h4 className="text-sm font-semibold">Moderation</h4>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant={review.status === 'approved' ? 'secondary' : 'default'}
                  onClick={handleApprove}
                  disabled={busy !== null || review.status === 'approved'}
                  className="gap-1.5"
                >
                  {busy === 'approve' ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Check className="h-3 w-3" />
                  )}
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant={review.status === 'rejected' ? 'secondary' : 'destructive'}
                  onClick={handleReject}
                  disabled={busy !== null || review.status === 'rejected'}
                  className="gap-1.5"
                >
                  {busy === 'reject' ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <X className="h-3 w-3" />
                  )}
                  Reject
                </Button>
              </div>

              <div className="flex items-center justify-between gap-3 pt-3 border-t border-border/60">
                <div>
                  <div className="text-sm font-medium">Show on landing page</div>
                  <div className="text-xs text-muted-foreground">
                    Only available for approved reviews. Toggles `is_featured`.
                  </div>
                </div>
                <Switch
                  checked={!!review.is_featured}
                  onCheckedChange={handleToggleFeatured}
                  disabled={busy !== null || review.status !== 'approved'}
                />
              </div>

              {review.is_featured && (
                <div className="flex items-center justify-between gap-3 pt-3 border-t border-border/60">
                  <div>
                    <div className="text-sm font-medium">Featured order</div>
                    <div className="text-xs text-muted-foreground">
                      Lower numbers appear first. Current: {review.featured_order}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleMoveOrder(-1)}
                      disabled={busy !== null || review.featured_order <= 0}
                    >
                      <ArrowUp className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleMoveOrder(1)}
                      disabled={busy !== null}
                    >
                      <ArrowDown className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-border/60">
                <Button size="sm" variant="outline" asChild className="gap-1.5">
                  <a href="/#testimonials" target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3 w-3" />
                    View on landing page
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-muted/60 flex items-center justify-center">
                <Star className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <h4 className="text-sm font-semibold">No review submitted yet</h4>
                <p className="text-xs text-muted-foreground">
                  Send an invitation to encourage feedback.
                </p>
              </div>
            </div>

            {invitation ? (
              <div className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <MailCheck className="h-4 w-4 text-primary" />
                  <span>
                    Invited via{' '}
                    <span className="font-medium">
                      {TRIGGER_LABELS[invitation.trigger_type] ?? invitation.trigger_type}
                    </span>{' '}
                    on{' '}
                    <span className="font-medium">
                      {format(new Date(invitation.sent_at), 'dd.MM.yy HH:mm')}
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  {invitation.clicked_at ? (
                    <>
                      <Check className="h-4 w-4 text-success" />
                      Clicked on {format(new Date(invitation.clicked_at), 'dd.MM.yy HH:mm')}
                    </>
                  ) : (
                    <>
                      <MailX className="h-4 w-4" />
                      Not yet clicked
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-border/60 bg-muted/20 p-3 flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="h-4 w-4" />
                Never invited
              </div>
            )}

            <Button
              size="sm"
              onClick={() => handleSendInvite(!!invitation)}
              disabled={busy !== null}
              className="gap-1.5"
            >
              {busy === 'invite' ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : invitation ? (
                <RefreshCw className="h-3 w-3" />
              ) : (
                <Send className="h-3 w-3" />
              )}
              {invitation ? 'Re-send invitation' : 'Send invitation'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="font-semibold text-foreground text-[11px] uppercase tracking-wide">
        {label}
      </div>
      <div className="text-muted-foreground mt-0.5">{value}</div>
    </div>
  );
}
